import { useState } from "react";
import { AnnouncementDoc, PracticeForm, PracticeField } from "../../mocks/hubTasks";

// ─── 공고문 뷰어 모달 ─────────────────────────────────────
export function AnnouncementModal({ doc, onClose }: { doc: AnnouncementDoc; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-white rounded-2xl overflow-hidden shadow-2xl">
        {/* 헤더 */}
        <div className="bg-[#1A1A1A] px-6 py-4 flex items-start justify-between shrink-0">
          <div>
            <div className="text-[10px] text-white/50 font-semibold uppercase tracking-widest mb-1">
              {doc.issuer} 공식 공고
            </div>
            <h2 className="text-white font-bold text-base leading-snug">{doc.title}</h2>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className="text-[11px] text-white/60">공고일: {doc.issueDate}</span>
              {doc.deadline && (
                <span className="text-[11px] text-amber-400/90 font-semibold flex items-center gap-1">
                  <i className="ri-alarm-warning-line text-xs"></i>마감: {doc.deadline}
                </span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white/80 cursor-pointer transition-colors mt-0.5">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        {/* 안내 배너 */}
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center gap-2 shrink-0">
          <i className="ri-lightbulb-flash-line text-amber-500 text-sm shrink-0"></i>
          <p className="text-amber-700 text-xs">
            이 공고문을 꼼꼼히 분석해 평가 기준과 필수 서류를 파악하세요. 심사 배점이 표시된 항목을 우선 확인하세요.
          </p>
        </div>
        {/* 본문 */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {doc.sections.map((section, i) => (
            <div key={i}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-4 rounded-full bg-[#1A1A1A] shrink-0"></div>
                <h3 className="text-[#1A1A1A] font-bold text-sm">{section.heading}</h3>
              </div>
              <div className="pl-3 border-l border-[#E0E0E0]">
                <p className="text-[#555555] text-sm leading-relaxed whitespace-pre-line">{section.content}</p>
              </div>
            </div>
          ))}
        </div>
        {/* 푸터 */}
        <div className="border-t border-[#EEEEEE] px-6 py-3 flex items-center justify-between shrink-0 bg-[#FAFAFA]">
          <span className="text-[11px] text-[#AAAAAA]">공식 공고문 | {doc.issuer}</span>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-[#1A1A1A] font-semibold bg-[#F0F0F0] hover:bg-[#E0E0E0] px-4 py-1.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 실습 양식 작성 모달 ─────────────────────────────────
export function PracticeFormModal({ form, onClose }: { form: PracticeForm; onClose: () => void }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const inputCls =
    "w-full bg-[#F9F9F9] border border-[#E0E0E0] focus:border-[#1A1A1A] outline-none text-[#1A1A1A] text-sm px-3 py-2 rounded-lg placeholder-[#BBBBBB] transition-colors";
  const textareaCls =
    "w-full bg-[#F9F9F9] border border-[#E0E0E0] focus:border-[#1A1A1A] outline-none text-[#1A1A1A] text-sm px-3 py-2.5 rounded-lg placeholder-[#BBBBBB] transition-colors resize-none leading-relaxed";

  const allRequiredFilled = form.fields.filter((f) => f.required).every((f) => values[f.id]?.trim());

  const renderField = (field: PracticeField) => {
    const val = values[field.id] ?? "";
    const onChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => setValues((prev) => ({ ...prev, [field.id]: e.target.value }));

    return (
      <div key={field.id} className="mb-5">
        <label className="block text-xs font-semibold text-[#555555] mb-1.5">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {field.type === "textarea" ? (
          <textarea
            value={val}
            onChange={onChange}
            placeholder={field.placeholder}
            rows={4}
            maxLength={field.maxLength ?? 500}
            className={textareaCls}
          />
        ) : field.type === "select" ? (
          <select value={val} onChange={onChange} className={inputCls}>
            <option value="">선택하세요</option>
            {field.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : (
          <input type={field.type} value={val} onChange={onChange} placeholder={field.placeholder} className={inputCls} />
        )}
        {field.hint && (
          <div className="mt-1.5 flex gap-1.5">
            <i className="ri-lightbulb-flash-line text-amber-500/80 text-xs mt-0.5 shrink-0"></i>
            <p className="text-xs text-[#888888] leading-relaxed">{field.hint}</p>
          </div>
        )}
        {field.type === "textarea" && field.maxLength && (
          <div className="text-right text-[10px] text-[#CCCCCC] mt-1">
            {val.length}/{field.maxLength}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col bg-white rounded-2xl overflow-hidden shadow-2xl">
        {/* 헤더 */}
        <div className="bg-[#F5F5F5] border-b border-[#E0E0E0] px-6 py-4 flex items-start justify-between shrink-0">
          <div>
            <div className="text-[10px] text-[#AAAAAA] font-semibold uppercase tracking-widest mb-1">
              직접 작성 실습
            </div>
            <h2 className="text-[#1A1A1A] font-bold text-base">{form.title}</h2>
            {form.subtitle && <p className="text-[11px] text-[#888888] mt-0.5">{form.subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#CCCCCC] hover:text-[#888888] cursor-pointer transition-colors mt-0.5"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {submitted ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-3xl"></i>
            </div>
            <h3 className="text-[#1A1A1A] font-bold text-lg mb-2">작성 완료!</h3>
            <p className="text-[#777777] text-sm leading-relaxed mb-6">
              실제 업무에서는 이 내용을 기반으로
              <br />
              AI 사전검토 후 결재를 제출하게 됩니다.
            </p>
            <div className="text-left bg-[#F9F9F9] rounded-xl border border-[#E0E0E0] p-4 w-full max-w-md">
              {form.fields
                .filter((f) => values[f.id])
                .map((f) => (
                  <div key={f.id} className="mb-2 last:mb-0">
                    <div className="text-[10px] text-[#AAAAAA] font-semibold mb-0.5">{f.label}</div>
                    <div className="text-xs text-[#555555] whitespace-pre-wrap line-clamp-2">{values[f.id]}</div>
                  </div>
                ))}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 text-sm text-white bg-[#1A1A1A] hover:bg-[#333333] px-6 py-2.5 rounded-xl cursor-pointer transition-colors whitespace-nowrap font-semibold"
            >
              닫기
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">{form.fields.map(renderField)}</div>
            <div className="border-t border-[#EEEEEE] px-6 py-3.5 flex items-center justify-between shrink-0 bg-[#FAFAFA]">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[#CCCCCC]">
                  필수 항목 {form.fields.filter((f) => f.required && values[f.id]?.trim()).length}/
                  {form.fields.filter((f) => f.required).length} 완료
                </span>
                {allRequiredFilled && (
                  <span className="text-[11px] text-emerald-500 font-semibold flex items-center gap-1">
                    <i className="ri-check-line"></i>제출 가능
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm text-[#888888] border border-[#E0E0E0] hover:bg-[#F0F0F0] px-4 py-1.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={() => setSubmitted(true)}
                  disabled={!allRequiredFilled}
                  className="text-sm text-white bg-[#1A1A1A] hover:bg-[#333333] disabled:opacity-30 disabled:cursor-not-allowed px-5 py-1.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap font-semibold"
                >
                  <i className="ri-save-line mr-1.5"></i>작성 완료
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
